import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useStripeStatus } from '@/hooks/useStripeStatus';
import { BusinessProfile } from '@/types/businessProfile';

interface BusinessInfo {
  name?: string;
  address?: {
    line1?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  };
}

export function useStripeBusinessSync() {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { accountId: stripeAccountId, isConnected } = useStripeStatus();

  const syncStripeBusinessInfo = async (): Promise<BusinessInfo | null> => {
    if (!isConnected || !stripeAccountId) {
      toast({
        title: "Not connected to Stripe",
        description: "Please connect your Stripe account first.",
        variant: "destructive"
      });
      return null;
    }
    
    setIsLoading(true);
    try {
      // Call the edge function to get Stripe account information
      const { data: sessionData } = await supabase.auth.getSession();
      
      if (!sessionData.session) {
        throw new Error("Authentication required");
      }
      
      const { data, error } = await supabase.functions.invoke('get-stripe-business-info', {
        body: { accountId: stripeAccountId },
        headers: {
          Authorization: `Bearer ${sessionData.session.access_token}`
        }
      });

      if (error) {
        console.error('Error fetching Stripe business info:', error);
        throw new Error(error.message || "Failed to fetch business information from Stripe");
      }
      
      if (!data?.business) {
        console.warn('No business information returned from Stripe');
        return null;
      }
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error("User not authenticated");
      }

      const { data: currentProfile } = await supabase
        .from('profiles')
        .select('ref_signup')
        .eq('id', user.id)
        .single();

      const updateData: any = {
        business_name: data.business.name || null,
        street_address: data.business.address?.line1 || null,
        city: data.business.address?.city || null,
        state: data.business.address?.state || null,
        zip_code: data.business.address?.postal_code || null,
        updated_at: new Date().toISOString()
      };

      if (currentProfile?.ref_signup) {
        updateData.ref_signup = currentProfile.ref_signup;
      }

      const { error: updateError } = await supabase.from('profiles')
        .update(updateData)
        .eq('id', user.id);
      
      if (updateError) {
        console.error('Error updating profile with Stripe data:', updateError);
        throw new Error("Failed to update profile with Stripe business information");
      }
      
      toast({
        title: "Business info synchronized",
        description: "Your business information has been updated from Stripe."
      });
      
      return data.business;
    } catch (error: any) {
      console.error('Error in syncStripeBusinessInfo:', error);
      toast({
        title: "Synchronization failed",
        description: error.message || "Failed to sync business information with Stripe",
        variant: "destructive"
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };
  
  return {
    isLoading,
    syncStripeBusinessInfo
  };
}